/**
 * StripePaymentForm — Inline card entry for ride payment.
 * Creates a PaymentIntent, collects card details via Stripe Elements,
 * and confirms the authorization before allowing ride creation.
 */
import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { CreditCard, Loader2, Shield, AlertCircle, CheckCircle, Lock } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_STYLE = {
  style: {
    base: {
      fontSize: "16px",
      color: "#1f2937",
      fontFamily: "system-ui, -apple-system, sans-serif",
      "::placeholder": { color: "#9ca3af" },
      iconColor: "#6b7280",
    },
    invalid: { color: "#dc2626", iconColor: "#dc2626" },
  },
};

function CardForm({ amount, currency, onPaymentComplete, onCancel, submitting, setSubmitting }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [authorized, setAuthorized] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements || processing) return;
    setError(null);
    setProcessing(true);
    setSubmitting(true);

    try {
      // 1. Create PaymentIntent on backend
      const token = localStorage.getItem("token") || localStorage.getItem("access_token");
      const piRes = await fetch(`${API_URL}/api/rides/payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount, currency }),
      });
      const piData = await piRes.json();
      if (!piData.success) {
        setError(piData.detail || piData.error || "Could not set up payment. Try again.");
        setProcessing(false);
        setSubmitting(false);
        return;
      }

      // 2. Confirm card payment (authorize only — manual capture)
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        piData.client_secret,
        { payment_method: { card: elements.getElement(CardElement) } }
      );

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        setSubmitting(false);
        return;
      }

      if (paymentIntent && (paymentIntent.status === "requires_capture" || paymentIntent.status === "succeeded")) {
        setAuthorized(true);
        onPaymentComplete(piData.payment_intent_id);
      } else {
        setError(`Unexpected payment status: ${paymentIntent?.status}. Please try again.`);
        setProcessing(false);
        setSubmitting(false);
      }
    } catch (e) {
      setError("Network error. Please check your connection and try again.");
      setProcessing(false);
      setSubmitting(false);
    }
  };

  if (authorized) {
    return (
      <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center" data-testid="payment-authorized">
        <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
        <p className="text-sm font-semibold text-emerald-700">Card authorized</p>
        <p className="text-xs text-emerald-600 mt-1">Your card has been verified. Creating your ride...</p>
      </div>
    );
  }

  const fmtAmount = currency === "USD"
    ? `$${(Number(amount) || 0).toFixed(2)} USD`
    : `J$${(Number(amount) || 0).toLocaleString()}`;

  return (
    <div data-testid="stripe-card-form">
      <div className="mb-3">
        <p className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
          <CreditCard className="w-4 h-4 text-emerald-600" /> Enter your card details
        </p>
        <div className="bg-white border-2 border-gray-200 rounded-xl p-4 focus-within:border-emerald-400 transition">
          <CardElement options={CARD_STYLE} onChange={(e) => { setCardComplete(e.complete); if (e.error) setError(e.error.message); else setError(null); }} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 rounded-lg p-2.5 mb-3 border border-red-200 flex items-start gap-2" data-testid="payment-error">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-2 mb-3 flex items-center justify-center gap-1.5">
        <Lock className="w-3 h-3 text-gray-400" />
        <p className="text-xs text-gray-500">
          Your card will be authorized for <strong>{fmtAmount}</strong>. You will only be charged when your ride is completed.
        </p>
      </div>

      <div className="flex gap-2">
        <button onClick={onCancel} disabled={processing}
          className="flex-1 py-3 border border-gray-200 rounded-xl text-sm text-gray-600 disabled:opacity-50" data-testid="payment-cancel-btn">
          Back
        </button>
        <button onClick={handleSubmit} disabled={!stripe || !cardComplete || processing}
          className="flex-1 py-3.5 bg-emerald-500 text-white rounded-xl text-sm font-bold hover:bg-emerald-600 transition disabled:opacity-50 flex items-center justify-center gap-1.5"
          data-testid="authorize-payment-btn">
          {processing
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Authorizing...</>
            : <><Shield className="w-4 h-4" /> Authorize {fmtAmount}</>
          }
        </button>
      </div>

      <div className="mt-2 flex items-center justify-center gap-1">
        <Shield className="w-3 h-3 text-gray-300" />
        <p className="text-[10px] text-gray-400">Secured by Stripe. Card details never touch our servers.</p>
      </div>
    </div>
  );
}

export default function StripePaymentForm({ amount, currency, onPaymentComplete, onCancel, submitting, setSubmitting }) {
  if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
    return (
      <div className="bg-red-50 rounded-xl p-4 border border-red-200 text-center">
        <AlertCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
        <p className="text-sm text-red-700 font-medium">Payment not available</p>
        <p className="text-xs text-red-600 mt-1">Payment service is not configured. Please contact support.</p>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <CardForm
        amount={amount}
        currency={currency}
        onPaymentComplete={onPaymentComplete}
        onCancel={onCancel}
        submitting={submitting}
        setSubmitting={setSubmitting}
      />
    </Elements>
  );
}
